// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

// CAUTION: The browser bundle of sharetribe-flex-sdk's transit-js has a known
// verbose-mode quirk where nested plain-object values inside the payload can be
// serialized as null (the Node bundle does not have this issue). Characterized
// May 4 2026 during Push 8 A.2c-2 debugging. If sending a payload that contains
// nested objects (e.g. ProseMirror editor JSON, custom parameter configs), the
// safest pattern is to JSON.stringify the nested-object field client-side and
// JSON.parse it server-side. See src/containers/DocumentTemplatesPage/
// DocumentTemplatesPage.duck.js (updateDocumentTemplate thunk) and
// server/api/update-template.js for the reference implementation.
const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Cancel a booking with optional partial refund logic.
// Body: { transactionId, cancelledBy, cancellationReason, cancellationReasonCode }
export const cancelBooking = body => {
  return post('/api/cancel-booking', body);
};

// Expire the current user's pending-payment transaction for a listing so the
// booking slot is freed immediately rather than waiting 15 minutes.
// Body: { listingId: string }
export const expirePendingTransaction = body => {
  return post('/api/expire-pending-transaction', body);
};

// Add a post-rental charge to a transaction's settlement.charges[].
// Body: { transactionId, type, amount, description, initiatedBy, verbalApproval, metadata }
// Note: the backend field is `verbalApproval`; the UI labels it "off-platform approval".
export const addCharge = body => {
  return post('/api/add-charge', body);
};

// Customer approves a pending charge — triggers immediate Stripe capture.
// Body: { transactionId, chargeId }
export const approveCharge = body => {
  return post('/api/approve-charge-v2', body);
};

// Customer declines a pending charge (no money captured, simple refusal).
// Body: { transactionId, chargeId, reasonCode? (optional), reasonText? (optional) }
export const declineCharge = body => {
  return post('/api/decline-charge', body);
};

// Customer disputes a captured off-platform charge (money already taken, BN arbitration).
// Body: { transactionId, chargeId, reasonCode, reasonText?, customerPhotos? }
// DEPRECATED — pre-9.5B-4 endpoint preserved for backward compat with existing
// transaction data. New flows route through `requestRefund` below.
export const disputeCharge = body => {
  return post('/api/dispute-charge-v2', body);
};

// Push 9.5B-4 — Customer requests refund on any captured charge. Replaces
// `disputeCharge` under the May 21, 2026 architecture lock.
// Body: { transactionId, chargeId, requestedAmountCents, reasonCode,
//         reasonText?, customerPhotos? }
// Returns: { success: true, refundRequestId, messagePosted }
export const requestRefund = body => {
  return post('/api/request-refund', body);
};

// Push 9.5B-4 — Provider responds to a customer refund request with one of
// three actions: refund-full (grant the requested amount in full),
// refund-partial (offer a lesser negotiated amount), or decline (refuse,
// optionally with counter-evidence photos).
// Body: { transactionId, chargeId, refundRequestId,
//         action: 'refund-full' | 'refund-partial' | 'decline',
//         refundAmountCents?  (required for refund-partial),
//         declineText?        (required for decline),
//         providerPhotos?     (optional for decline) }
// Returns: { success: true, action, stripeRefundId, messagePosted }
//   - stripeRefundId is null for decline
export const respondToRefundRequest = body => {
  return post('/api/respond-to-refund-request', body);
};

// Push 9.5B-4 — Provider issues a free-form refund against the base booking
// total (not tied to any specific Trip Settlement charge). Use case: trip
// cut short by 2 hours, provider refunds proportionally. Refundable cap is
// computed from Stripe (pi.amount_received - pi.amount_refunded) — local
// metadata is for display only. Customer service fee stays as captured;
// for proportional fee adjustment, use the modification flow instead.
// Body: { transactionId, amountCents, reasonCode?, reasonText? }
// Returns: { success: true, refundId, stripeRefundId, messagePosted }
export const refundBaseBooking = body => {
  return post('/api/refund-base-booking', body);
};

// Provider issues a full or partial refund for a captured charge.
// Body: { transactionId, chargeId, amountCents? (omit for full), reasonCode?, reasonText? }
export const refundCharge = body => {
  return post('/api/refund-charge', body);
};

// Persist the base booking Stripe PaymentIntent ID to settlement metadata so
// cancel-booking can issue in-house refunds later.
// Body: { transactionId: string, paymentIntentId: "pi_XXX" }
export const recordBasePayment = body => {
  return post('/api/record-base-payment', body);
};

// Preview a booking modification — returns old/new line-item breakdown, delta,
// fee adjustments, and availability-conflict flag. Read-only; does not fire
// any transition. Used by ModifyBookingModal to show the user what their
// proposed change will cost before submitting the actual request.
// Body: { transactionId, newOrderData: { bookingStart, bookingEnd, selectedAddons?, deliveryZoneFee?, selectedSlotPrice? } }
export const previewModification = body => {
  return post('/api/preview-modification', body);
};

// Submit a booking modification request — fires the request transition,
// stores the request record in protectedData.modification.requests[],
// posts a system message to the transaction thread (auto-triggers
// Sharetribe's NewMessage email to the other party). Server-side endpoint
// is in server/api/request-modification.js (Push 7A.2). Body shape:
//   { transactionId, newOrderData: { bookingStart, bookingEnd, selectedAddons?, deliveryZoneFee?, selectedSlotPrice? }, notes? }
export const requestModification = body => {
  return post('/api/request-modification', body);
};

// Respond to a pending modification request — accept or decline.
// Accept path captures Stripe delta or issues partial refund (Stripe-first
// ordering with idempotency keys), then fires the accept transition with
// updated bookingStart/End and lineItems. Decline path returns transaction
// to state/accepted with no money movement. Server-side endpoint in
// server/api/respond-to-modification.js (Push 7A.2). Body shape:
//   { transactionId, requestId, action: 'accept' | 'decline', preview? }
export const respondToModification = body => {
  return post('/api/respond-to-modification', body);
};

// Withdraw a pending modification request — only the original requester
// can withdraw. No Stripe operations (fires before any money movement).
// Server-side endpoint in server/api/withdraw-modification.js (Push 7A.2).
// Body shape: { transactionId, requestId }
export const withdrawModification = body => {
  return post('/api/withdraw-modification', body);
};

// Create a Stripe Identity VerificationSession for the authenticated user.
// Returns { clientSecret } for the embedded Identity modal. Server-side
// endpoint in server/api/identity-verification-session.js (Push 7C Phase B).
// Returns a 409 with { error: 'Already verified' } if the user is already
// verified — the page handles that as the already-verified state.
export const createIdentityVerificationSession = () => {
  return post('/api/identity-verification-session/create', {});
};

// ─── Push 8 Phase A.2a — Document templates ────────────────────────────────
// Provider-scoped CRUD for document templates (rental agreements, waivers,
// etc.). All four endpoints require an authenticated provider session;
// 401 on unauthenticated callers. Server-side endpoints in server/api/.

// List the caller's templates. Defaults to active set; pass { archived: true }
// to fetch the archived set instead. Returns { templates }.
export const listTemplates = ({ archived = false } = {}) => {
  return post('/api/list-templates', { archived });
};

// Create a new template + first version row in a single transaction.
// Body shape: { name, document_type, signer_role, content_html?, content_json? }
// Returns { template, version }.
export const createTemplate = body => {
  return post('/api/create-template', body);
};

// Fetch a single template + its latest version row for the editor view.
// The list endpoint stays content-free for performance; this endpoint
// returns full editable content. Returns { template, latestVersion }.
export const getTemplate = id => {
  return post('/api/get-template', { id });
};

// Update header fields and/or append a new content version row. Both
// content_html and content_json must be supplied together when updating
// content. Body shape: { id, name?, active?, auto_attach?, listing_ids?,
// custom_parameters?, content_html?, content_json? }.
// Returns { template, version, contentVersionBumped }.
export const updateTemplate = body => {
  return post('/api/update-template', body);
};

// Soft-delete a template (sets deleted_at). RESTRICT FK on document_pdfs
// preserves PDF references even after archive. Returns { success, archivedAt }.
export const archiveTemplate = id => {
  return post('/api/archive-template', { id });
};

// Reverse a soft-delete (clears deleted_at). Returns { success, restoredAt,
// template } — the full row is included so the client can patch its slice
// without a separate refetch. 400 if the template is not currently archived.
export const restoreTemplate = id => {
  return post('/api/restore-template', { id });
};

// ─── Push 8 Phase A.4c — Signing link verification ─────────────────────────
// Public, unauthenticated endpoint that resolves a signing-link opaque token
// to its full context (link row + template + transaction). Used by
// SignDocumentPage's loadData to populate the Preliminary page.
//
// The fetch wrapper (`request`) parses the response based on Content-Type
// and throws an Error with the response body's properties merged in for any
// status >= 400. SignDocumentPage's duck inspects the thrown error's
// `status` field to map HTTP codes → error code constants
// (NOT_FOUND / EXPIRED / REVOKED / INVALID / SERVER_ERROR).
//
// Body: { token: string } — the opaque signing_token from the URL path
// Returns 200: { link, template, transaction }
export const verifySignLink = token => {
  return post('/api/sign-link/verify', { token });
};

// ─── Push 8 Phase A.4d — Signing link submission ───────────────────────────
// Public, unauthenticated submit endpoint that finalizes a customer signing
// (Waivers/Agreements) OR a Contract signing where the caller's Sharetribe
// session matches link.customer_id. Writes document_pending_signatures +
// document_signing_audit_log in one Knex transaction; both rows share a
// freshly-generated `documentInstanceId`. The signing link is NOT voided —
// multi-signer flow allows N submissions per link.
//
// Body shape:
//   { token, signer_name, signer_email, customer_input_data, captures }
// Returns 200: { documentInstanceId, submittedAt }
//
// Error envelope mirrors verifySignLink (404/410/410/401/500). Contract
// signings additionally return 401 with `error: 'Authentication required ...'`
// or `error: 'Authentication mismatch ...'` when the session doesn't match
// link.customer_id. The duck's mapErrorBodyToCode handles the dispatch.
export const submitSignLink = body => {
  return post('/api/sign-link/submit', body);
};

// ─── Push 8 Phase A.4e-1a — Customer-side Documents subsection ─────────────
// Returns the per-link signing status for the booking confirmation page's
// Documents subsection. Auth: the caller's Sharetribe session must match the
// transaction's customer relationship; otherwise 403. Voided links are
// filtered server-side so this endpoint never returns revoked rows.
//
// Body: { transactionId: string }
// Returns 200: { signingLinks: [{ linkId, templateId, templateName, documentType,
//   signerRole, partyRole, signingUrl, expiresAt, submissions: [...] | null,
//   submissionCount, hasSignedSubmission, lastSignedAt, mergeFailure }] }
// Push 9 Sub-phase C.2b-ii: response shape mirrors provider-signed-documents
// (nested submissions[] per link). Privacy split: submissions[] populated
// only for inspection links (party_role IS NOT NULL); null otherwise.
// Errors: 400 (missing transactionId), 401 (no session), 403 (wrong user),
// 404 (transaction not found), 500 (server).
export const customerSignedDocuments = body => {
  return post('/api/customer-signed-documents', body);
};

// ─── Push 8 Phase A.4e-1b — Customer-side View modal detail ────────────────
// Lazy-fetched on the View click for one signing link's signed submission.
// Server selects the caller's own submission via signer_user_id / signer_email
// match against the link's document_pending_signatures rows. 404 when the
// caller never signed personally (per Q-1b-a; we don't surface guest
// submissions to the customer under a "your document" UI).
//
// Body: { linkId: string }
// Returns 200: { pendingSignature, auditLog, template }
// Errors: 400 (missing linkId), 401 (no session), 403 (wrong customer),
// 404 (no own submission), 500 (server).
export const customerSignedDocumentDetail = body => {
  return post('/api/customer-signed-document-detail', body);
};

// ─── Push 8 Phase A.4e-2a — Provider-side Documents section list ───────────
// Returns one row per non-voided signing link on the transaction, with the
// nested submissions array filled in (provider sees ALL submissions, not
// just their own — Renter Contract is 1:1, Waivers/Agreements may have N
// guest-signer rows). Auth: caller's session must match the transaction's
// provider relationship; otherwise 403.
//
// Body: { transactionId: string }
// Returns 200: { signingLinks: [{ linkId, templateId, templateName,
//                                 documentType, signerRole, signingUrl,
//                                 expiresAt, submissions: [{submissionId,
//                                 documentInstanceId, signerName,
//                                 signerEmail, signedAt}] }] }
// Errors: 400 (missing transactionId), 401 (no session), 403 (wrong user),
// 404 (transaction not found), 500 (server).
export const providerSignedDocuments = body => {
  return post('/api/provider-signed-documents', body);
};

// ─── Push 8 Phase A.4e-2a — Provider-side View modal detail ────────────────
// Keyed by submissionId (the row PK), not by linkId — provider's UI lists
// each submission individually so the click handler always has the
// submissionId in hand. Server auth-checks the caller is the provider on
// the submission's transaction. No signer-match filter (asymmetric vs
// customer-side: provider must see all submissions including guests').
//
// Body: { submissionId: string }
// Returns 200: { pendingSignature, auditLog, template, transaction }
// Errors: 400 (missing submissionId), 401 (no session), 403 (wrong provider),
// 404 (no submission found), 500 (server).
export const providerSignedDocumentDetail = body => {
  return post('/api/provider-signed-document-detail', body);
};

// ─── Push 8 Phase A.4e-2b — Merge-failure detection + notification ─────────
// recordMergeFailure: public endpoint called by SignDocumentDocumentPage
// when its hasRequiredBrokenChips flag fires. Idempotent (existing
// unresolved row returns its id; new detection inserts).
//
// Body: { signingLinkId, missingFieldKeys: string[],
//         isAuthenticatedBookingCustomer: boolean }
// Returns 200: { failureId, isExisting }
export const recordMergeFailure = body => {
  return post('/api/record-merge-failure', body);
};

// mergeFailureGuestEmail: public endpoint called by the blocking-error
// page's guest-email-capture form. Updates guest_email on the latest
// unresolved failure for the link.
//
// Body: { signingLinkId, guestEmail }
// Returns 200: { success: true }
export const mergeFailureGuestEmail = body => {
  return post('/api/merge-failure-guest-email', body);
};

// providerHasMergeFailures: authenticated endpoint backing the Topbar
// alert icon + My Bookings per-card visual. Returns binary visibility
// flag + the list of affected transaction IDs in one roundtrip.
//
// Body: {}
// Returns 200: { hasUnresolvedFailures: boolean, affectedTxIds: string[] }
export const providerHasMergeFailures = () => {
  return post('/api/provider-has-merge-failures', {});
};

// markMergeFailureMessagePosted: public endpoint stamping
// message_posted_at after the lazy-posting frontend successfully writes
// the activity-feed message via trustedSdk.messages.send.
//
// Body: { failureId }
// Returns 200: { success: true } (idempotent — no-op if already stamped)
export const markMergeFailureMessagePosted = body => {
  return post('/api/mark-merge-failure-message-posted', body);
};

// ─── Push 8 Phase A.4f — Manual completion workflow ────────────────────────
// mergeFailureFields: provider-only modal-hydration endpoint. Returns the
// affected template name + deduplicated missing-field-keys list +
// pre-fill values for any existing overrides + deduplicated guest-email
// list (from A.4e-2b's multi-row capture).
//
// Body: { transactionId, signingLinkId }
// Returns 200: { templateName, missingFieldKeys: string[],
//                currentOverrides: {fieldKey: value},
//                guestEmails: string[] }
export const mergeFailureFields = body => {
  return post('/api/merge-failure-fields', body);
};

// saveMergeFailureOverrides: provider-only upsert. Each entry in the
// overrides map writes one row to reservation_field_overrides
// (UNIQUE on (transaction_id, field_key) — latest value wins).
// Empty values are silently dropped (no semantic for "clear an
// override" in V1; providers correct by overwriting).
//
// Body: { transactionId, overrides: {fieldKey: value} }
// Returns 200: { savedCount: N }
export const saveMergeFailureOverrides = body => {
  return post('/api/save-merge-failure-overrides', body);
};

// sendForSigning: provider-only commit + notify. Validates coverage
// (all missing_field_keys covered by non-empty overrides), marks all
// unresolved failure rows resolved, posts the customer-notification
// activity-feed message via the provider's session, returns the
// reconstructed signing URL for the modal's success state.
//
// Body: { signingLinkId, messageContent }
// Returns 200: { resolvedFailureCount, signingUrl, guestEmails: string[] }
// Errors: 400 with { missingFieldKeys: [...] } when coverage validation
// fails (provider didn't fill all required fields).
export const sendForSigning = body => {
  return post('/api/send-for-signing', body);
};

// ─── Push 9 Sub-phase B — inspection signing endpoints ─────────────────────

// startInspectionSigning: late-binding creation of a document_signing_links
// row at "Start Check-In" / "Start Check-Out" click. Section 4 override:
// provider may initiate partyRole='customer' for at-dock signing on the
// provider's device. 409 with { error: 'inspection_already_started', link }
// when a concurrent click wins the race (handle by routing to existing link).
//
// Body: { transactionId, templateId, partyRole: 'provider' | 'customer' }
// Returns 200: { signingLinkId, url }
// Errors: 400 / 401 / 403 / 404 / 409 / 500 (see endpoint source for codes)
export const startInspectionSigning = body => {
  return post('/api/start-inspection-signing', body);
};

// inspectionSigningStatus: read endpoint for the inspection signing-link
// status feed. Returns party_role-distinguished rows with submitted state
// + counterpartEngagedAt lock + agentAttestation marker. The signingToken
// is returned only for the row whose party_role matches the caller's
// relationship (with the Section 4 provider-as-customer override: provider
// also receives the customer-party-role token).
//
// Body: { transactionId }
// Returns 200: { inspectionSigningLinks: [{ id, partyRole, templateId,
//   templateName, documentType, signerRole, submitted, submittedAt,
//   counterpartEngagedAt, agentAttestation, signingToken? }] }
export const inspectionSigningStatus = body => {
  return post('/api/inspection-signing-status', body);
};

// forkTemplate: provider-only clone of an existing template with
// caller-supplied overrides for name + document_type + signer_role. Replaces
// the prior duplicateTemplate wrapper (Sub-phase B polish — Cooper's
// workflow gap: forking a Check-in into a Check-out required overriding
// document_type). Pure-duplicate semantics are achieved by leaving the
// modal defaults unchanged (same name+suffix, same type, same role).
//
// Body: { templateId, newName, newDocumentType, newSignerRole }
// Returns 200: { newTemplateId }
export const forkTemplate = body => {
  return post('/api/fork-template', body);
};

// ─── Push 9 Sub-phase C.1 — R2 signed-URL endpoints ────────────────────────

// requestR2SignedUploadUrl: short-lived presigned PUT URLs for a single
// photo (one original + one thumbnail) attached to an inspection signing
// link. Caller must be one of the two parties on the link. Server
// generates the photoId + keys; client uploads via fetch() PUT directly
// to R2 with the returned URLs.
//
// Body: { signingLinkId, mimeType }
// Returns 200: { photoId, originalKey, thumbnailKey, uploadUrls: { original, thumbnail }, expiresAt }
// Errors: 400 / 401 / 403 / 404 / 410 / 500
export const requestR2SignedUploadUrl = body => {
  return post('/api/r2/signed-upload-url', body);
};

// requestR2SignedDownloadUrl: 1-hour presigned GET URL for a single photo
// key. Used by PhotoUploadChip's readOnly thumbnail rendering and by
// PhotoLightboxModal's full-resolution image fetch.
//
// Body: { photoKey }
// Returns 200: { url, expiresAt }
// Errors: 400 / 401 / 403 / 404 / 500
export const requestR2SignedDownloadUrl = body => {
  return post('/api/r2/signed-download-url', body);
};

// ─── Push 9.5B-1 — R2 settlement photo endpoints ───────────────────────────

// requestR2SignedSettlementUploadUrl: short-lived presigned PUT URLs for a
// single settlement-related photo (fuel receipt, damage-claim, refund
// evidence, etc.) attached to a transaction. Caller must be the customer
// or provider on the transaction. Server generates the photoId + keys;
// client uploads original + thumbnail via fetch() PUT directly to R2.
//
// Body: { transactionId, scopeType, scopeId, mimeType }
//   scopeType: 'charge' | 'damage-claim' | 'refund-request' | 'general'
//   scopeId:   UUID, or the literal 'general' when scopeType === 'general'
// Returns 200: { photoId, originalKey, thumbnailKey, uploadUrls: { original, thumbnail }, expiresAt }
// Errors: 400 / 401 / 403 / 500
export const requestR2SignedSettlementUploadUrl = body => {
  return post('/api/r2/signed-upload-url-settlement', body);
};

// requestR2SignedSettlementDownloadUrl: 1-hour presigned GET URL for a
// settlement photo key minted by the matching upload endpoint. Caller
// must be the customer or provider on the named transaction.
//
// Body: { transactionId, photoKey }
// Returns 200: { url, expiresAt }
// Errors: 400 / 401 / 403 / 500
export const requestR2SignedSettlementDownloadUrl = body => {
  return post('/api/r2/signed-download-url-settlement', body);
};

// ─── Push 9 Sub-phase C.2a — inspection reconciliation read endpoint ───────

// inspectionReconciliationGet: returns the persisted reconciliation row
// for an inspection signing pair (provider Party A + customer Party B on
// the same transaction + template). Row is written by sign-link-submit's
// eager-trigger upsert inside the locked transaction when the second-of-
// two submissions lands. Caller must be one of the transaction's parties
// (customer or provider). Operator access is scoped separately when the
// operator dashboard ships.
//
// Body: { transactionId, templateId }
// Returns 200: { reconciliation: { id, transaction_id, template_id,
//   party_a_signing_link_id, party_b_signing_link_id, comparison_map,
//   has_disagreements, resolved_at, created_at, ... } }
// Returns 404 { error: 'not_found' } when no row exists yet (only one
//   party has submitted).
// Errors: 400 / 401 / 403 / 404 / 500
export const inspectionReconciliationGet = body => {
  return post('/api/inspection-reconciliation/get', body);
};
